import type { ContractFunctionName, Hex } from 'viem'

import type { Prettify } from '@repo/utils'

import type { MorphoMarketV1AdapterV2Abi } from '../abis.js'
import type { ReverseMapping } from './types.js'

/** Type for function names in the Morpho Market V1 Adapter V2 ABI */
export type MorphoMarketV1AdapterV2FunctionName = ContractFunctionName<
  typeof MorphoMarketV1AdapterV2Abi
>

/** Mapping from selector → name for functions that call `timelocked()` on the adapter */
export const MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_SELECTORS = {
  '0xb2e32848': 'abdicate',
  '0x3b2de5ac': 'burnShares',
  '0x5c1a1a4f': 'decreaseTimelock',
  '0x47966291': 'increaseTimelock',
  '0x2b30997b': 'setSkimRecipient'
} as const satisfies Record<Hex, MorphoMarketV1AdapterV2FunctionName>

type AdapterTimelockSelectorMap = typeof MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_SELECTORS

/** Type for functions that call `timelocked()` on the adapter */
export type MorphoMarketV1AdapterV2TimelockType =
  AdapterTimelockSelectorMap[keyof AdapterTimelockSelectorMap]

/** Mapping from name → selector for functions that call `timelocked()` on the adapter */
export const MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_NAME_TO_SELECTOR = Object.fromEntries(
  Object.entries(MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_SELECTORS).map(([hex, name]) => [name, hex])
) as Prettify<ReverseMapping<typeof MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_SELECTORS>>

/** Ordered array of all timelocked adapter function names */
export const MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_FUNCTIONS = Object.values(
  MORPHO_MARKET_V1_ADAPTER_V2_TIMELOCKED_SELECTORS
) as readonly MorphoMarketV1AdapterV2TimelockType[]

/**
 * Adapter timelocks that have a standalone duration to display and edit. `decreaseTimelock`
 * is omitted because the adapter derives its submit delay from the selector being
 * decreased — it has no timelock of its own.
 */
export const MORPHO_MARKET_V1_ADAPTER_V2_EDITABLE_TIMELOCKS: readonly {
  timelockType: MorphoMarketV1AdapterV2TimelockType
  selector: Hex
}[] = [
  { timelockType: 'abdicate', selector: '0xb2e32848' },
  { timelockType: 'burnShares', selector: '0x3b2de5ac' },
  { timelockType: 'increaseTimelock', selector: '0x47966291' },
  { timelockType: 'setSkimRecipient', selector: '0x2b30997b' }
] as const
